import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import styles from './styles.module.scss';
import stylesDashboard from '../../styles/dashboard.module.scss';

import { useGetSensorsDatesQuery, useGetSensorsSummaryQuery } from '../../store/api/sensors';
import { useGetNotesQuery } from '../../store/api/notes';
import { updateNotes } from '../../store/slices/notes';
import { mergeSensorsDates } from '../../utils/sensors';
import {
  GetReportTitle,
  GetTimelineReportItems,
  GetTimelineReportItemsVegetation,
  MergeReportArrays,
  MergeReportArraysVegetation,
} from './lib/index.jsx';
import { getSensorImagesForZip } from '../../utils/sensor';
import { TimelineMinNumberOfDaysReport } from './constants/index.jsx';
import {
  BreakpointMobile,
  BreakpointMobileUp,
  BreakpointTablet,
  BreakpointTabletUp,
} from '../../constants/ui';
import { ImageBoxes } from '../ImageBox/index.jsx';
import { useViewport } from '../../hooks';
import { AiAnalyticsMessage } from '../AiAnalytics/index.jsx';
import { Notes } from '../Notes/index.jsx';
import { TimelineList } from './index.jsx';
import { Metrics, MetricsCharts, MetricsVegetation, MetricsVegetationCharts } from '../Metrics/index.jsx';
import { WeatherForecast } from '../Weather/index.jsx';
import { PageSwipeMessage } from '../PageSwipeMessage/index.jsx';

const Timeline = ({ t, sensor, sensorFullData = [], dates, crop, showSoilData, isVegetation = false }) => {
  const dispatch = useDispatch();
  const { widthViewport } = useViewport();

  const scrollRef = useRef(null);

  const { selectedSensor, selectedSecondarySensor, selectedDate, nextSensor, prevSensor } = useSelector(
    (state) => state.sensors,
  );
  const { notesList } = useSelector((state) => state.notes);

  const [urls, setUrls] = useState({});
  const [urlsCompared, setUrlsCompared] = useState({});
  const [currentDateIndex, setCurrentDateIndex] = useState(null);
  const [sensorCompared, setSensorCompared] = useState({
    id: '',
    imgNDVI: {},
    imgDay: {},
    imgNight: {},
    phenostage: t('sensor.noStageAdded'),
  });
  const [dataReport, setDataReport] = useState(null);
  const [time, setTime] = useState(null);
  const [secondaryTime, setSecondaryTime] = useState(null);

  const { data: datesCompared = [] } = useGetSensorsDatesQuery(
    { location_id: selectedSecondarySensor },
    { skip: !selectedSecondarySensor, refetchOnMountOrArgChange: true },
  );

  const { data: notes } = useGetNotesQuery(
    { location_id: selectedSensor, ordering: 'created_at' },
    { skip: !selectedSensor, refetchOnMountOrArgChange: true },
  );

  const {
    data: sensorFullDataCompared = [],
    isSuccess: isDataComparedSuccess,
    isFetching: isDataComparedLoading,
  } = useGetSensorsSummaryQuery(
    { location_id: selectedSecondarySensor, ordering: 'date' },
    { skip: !selectedSecondarySensor },
  );

  const mergedDates = useMemo(() => mergeSensorsDates(dates, datesCompared), [dates, datesCompared]);

  const changeTime = (time) => {
    setTime(time);
  };

  const changeSecondaryTime = (time) => {
    setSecondaryTime(time);
  };

  useEffect(() => {
    if (
      isDataComparedLoading ||
      !isDataComparedSuccess ||
      !sensorFullDataCompared ||
      sensorFullDataCompared.length === 0 ||
      !selectedDate
    )
      return;

    if (sensorFullDataCompared.length > 0) {
      const sensorCompared = sensorFullDataCompared.find((item) => item.date === selectedDate);
      setSensorCompared(sensorCompared ? sensorCompared : null);
    }
  }, [sensorFullDataCompared, isDataComparedLoading, isDataComparedSuccess, selectedDate]);

  useEffect(() => {
    if (!sensorFullData || isDataComparedLoading || !dates) return;
    const indexImage = sensorFullData.findIndex(({ date }) => selectedDate === date);
    setCurrentDateIndex(indexImage);
    setUrls(getSensorImagesForZip(sensorFullData));
  }, [sensorFullData]);

  // Report charts data
  useEffect(() => {
    if (!sensorFullData || !selectedDate || isDataComparedLoading) return;

    // Main sensor report data
    const selectedIndex = sensorFullData.findIndex((item) => item.date === selectedDate);
    if (selectedIndex < TimelineMinNumberOfDaysReport) {
      setDataReport([]);
      return;
    }

    let report = isVegetation
      ? GetTimelineReportItemsVegetation(sensorFullData, selectedIndex, time)
      : GetTimelineReportItems(sensorFullData, selectedIndex);

    let selectedIndexCompared = null;
    let reportCompared = null;

    // Compared sensor report data
    if (selectedSecondarySensor && sensorFullDataCompared.length && secondaryTime) {
      selectedIndexCompared = sensorFullDataCompared.findIndex((item) => item.date === selectedDate);
      reportCompared = isVegetation
        ? GetTimelineReportItemsVegetation(sensorFullDataCompared, selectedIndexCompared, secondaryTime)
        : GetTimelineReportItems(sensorFullDataCompared, selectedIndexCompared);
    }

    if (reportCompared) {
      report = isVegetation
        ? MergeReportArraysVegetation(report, reportCompared)
        : MergeReportArrays(report, reportCompared);
    }

    setDataReport(report);
  }, [sensorFullData, selectedDate, selectedSecondarySensor, isDataComparedLoading, time, secondaryTime]);

  useEffect(() => {
    if (
      !selectedSecondarySensor ||
      (selectedSecondarySensor &&
        (isDataComparedLoading || !isDataComparedSuccess || !sensorFullDataCompared))
    )
      return;
    setUrlsCompared(getSensorImagesForZip(sensorFullDataCompared));
  }, [isDataComparedLoading, isDataComparedSuccess, sensorFullDataCompared, selectedSecondarySensor]);

  // Notes
  useEffect(() => {
    if (notes) {
      dispatch(updateNotes(notes));
    }
  }, [dispatch, notes, notesList]);

  return (
    <div
      className={`${styles.timelineContainer} ${stylesDashboard.sensorContainerInner} custom-scroll`}
      ref={scrollRef}
    >
      {/*Photo*/}
      <div className={styles.timelineImageContainer}>
        <ImageBoxes
          mergedDates={mergedDates}
          urls={urls}
          urlsCompared={urlsCompared}
          sensor={sensor}
          sensorCompared={sensorCompared}
          sensorFullDataCompared={sensorFullDataCompared}
          isVegetation={isVegetation}
          changeTime={(value) => changeTime(value)}
          changeSecondaryTime={(value) => changeSecondaryTime(value)}
          t={t}
        />

        {/*Notes*/}
        {!selectedSecondarySensor && widthViewport > BreakpointTablet && (
          <div className={styles.timelineNotesContainer}>
            {selectedSensor && (
              <Notes
                locationId={selectedSensor}
                notes={notes}
                key={selectedSensor}
                isImageBox={true}
                t={t}
              ></Notes>
            )}
          </div>
        )}
      </div>

      {/*Timeline*/}
      <TimelineList
        dates={mergedDates}
        data={sensorFullData}
        dataCompared={sensorFullDataCompared}
        currentDateIndex={currentDateIndex}
        t={t}
      />

      <div className={styles.timelineContent}>
        {/*Metrics*/}
        {isVegetation ? (
          <MetricsVegetation
            t={t}
            sensor={sensor}
            sensorCompared={sensorCompared}
            time={time}
            secondaryTime={secondaryTime}
          />
        ) : (
          <Metrics t={t} sensor={sensor} sensorCompared={sensorCompared} showSoilData={showSoilData} />
        )}

        {/*AI BBCH*/}
        {sensor && sensor.diseaseAI && <AiAnalyticsMessage diseaseAI={sensor.diseaseAI} t={t} />}

        {/*Charts*/}
        {dataReport && dataReport.dates && (
          <>
            {((isVegetation && time && widthViewport > BreakpointMobile) ||
              !(isVegetation && widthViewport > BreakpointMobile)) && (
              <h2 className={styles.timelineContentTitle}>
                {GetReportTitle({ t, startDate: dataReport.dates[0], endDate: dataReport.dates[1] })}
              </h2>
            )}
            {isVegetation ? (
              <MetricsVegetationCharts data={dataReport} t={t} />
            ) : (
              <MetricsCharts data={dataReport} crop={crop} showSoilData={showSoilData} t={t} />
            )}
          </>
        )}

        {/*Weather*/}
        {sensor && sensor.location && <WeatherForecast location={sensor.location} t={t} />}

        {/*Notes (Mobile)*/}
        {widthViewport < BreakpointTabletUp && selectedSensor && (
          <div className={styles.timelineContentBlock}>
            <h2 className={styles.timelineContentTitle}>{t('notes.title')}</h2>
            <Notes locationId={selectedSensor} notes={notes} key={selectedSensor} t={t}></Notes>
          </div>
        )}
      </div>

      {widthViewport < BreakpointMobileUp &&
        ((nextSensor && nextSensor.id) || (prevSensor && prevSensor.id)) && (
          <PageSwipeMessage scrollRef={scrollRef} />
        )}
    </div>
  );
};

export default Timeline;
